import type { AdminUsage, LimitKey, LimitRow, MyUsage, QueueView } from '@live-stream/shared'
import type { HttpClient } from '../http.js'

/** A person an admin looked a limit up for, by email. */
export interface LimitUser {
  id: string
  email: string
  displayName: string
}

export interface LimitsView {
  limits: LimitRow[]
  /** Set when the limits are one person's. */
  user: LimitUser | null
}

/** What the server's use is limited to: a person's month, and the admin's say over it. */
export class Limits {
  constructor(private readonly http: HttpClient) {}

  /** The signed-in person's month against their limits. */
  async myUsage(): Promise<MyUsage> {
    return (await this.http.get<{ usage: MyUsage }>('/usage/me')).usage
  }

  /** Admins: every limit in force, for one person when `userEmail` is given. */
  async list(userEmail?: string): Promise<LimitsView> {
    const q = userEmail ? `?userEmail=${encodeURIComponent(userEmail)}` : ''
    return this.http.get(`/admin/limits${q}`)
  }

  /** Admins: set a limit for everyone, or for one person. 0 is no limit (the AI budgets: none may be spent). */
  async set(key: LimitKey, value: number, userEmail?: string): Promise<LimitsView> {
    return this.http.put(`/admin/limits/${key}`, { value, ...(userEmail ? { userEmail } : {}) })
  }

  /** Admins: take a set limit away, back to the server's or the default. */
  async reset(key: LimitKey, userEmail?: string): Promise<LimitsView> {
    const q = userEmail ? `?userEmail=${encodeURIComponent(userEmail)}` : ''
    return this.http.delete(`/admin/limits/${key}${q}`)
  }

  /** Admins: this month across the server, and who used most. */
  async adminUsage(): Promise<AdminUsage> {
    return (await this.http.get<{ usage: AdminUsage }>('/admin/usage')).usage
  }

  /** Admins: the video queue: the counts, whether it is paused, the jobs given up on, the latest log. */
  async queue(): Promise<QueueView> {
    return (await this.http.get<{ queue: QueueView }>('/admin/queue')).queue
  }

  /** Admins: stop the queue taking new jobs (the running ones finish). */
  async pauseQueue(): Promise<QueueView> {
    return (await this.http.post<{ queue: QueueView }>('/admin/queue/pause')).queue
  }

  /** Admins: start the queue again. */
  async resumeQueue(): Promise<QueueView> {
    return (await this.http.post<{ queue: QueueView }>('/admin/queue/resume')).queue
  }

  /** Admins: run a failed job again. */
  async retryJob(id: string): Promise<QueueView> {
    return (
      await this.http.post<{ queue: QueueView }>(
        `/admin/queue/jobs/${encodeURIComponent(id)}/retry`,
      )
    ).queue
  }

  /** Admins: give up on a failed job for good. */
  async discardJob(id: string): Promise<QueueView> {
    return (
      await this.http.post<{ queue: QueueView }>(
        `/admin/queue/jobs/${encodeURIComponent(id)}/discard`,
      )
    ).queue
  }
}
